import React from 'react'
import {
  AppBar,
  Box,
  Button,
  ButtonProps,
  Container,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  Typography,
  Link,
} from '@common/components'
import personIcon from '@assets/svg/person-uno.svg'
import homeIcon from '@assets/svg/home-icon.svg'
import duoPersonIcon from '@assets/svg/duo-person-icon.svg'
import feedbackIcon from '@assets/svg/feedback-icon.svg'
import rulerIcon from '@assets/svg/ruler-icon.svg'
import { useAppSelector, useMediaQuery, useTranslation } from '@common/hooks'
import {
  COMMUNITY_ROUTE,
  DASHBOARD_ROUTE,
  EVENT_ROUTE,
  FEEDBACK_ROUTE,
  HOME_ROUTE,
  LANDING_ROUTE,
  LOGIN_ROUTE,
  MAIN_ROUTE,
  MY_CLASSROOMS_ROUTE,
  NOTES_ROUTE,
  PRICING_ROUTE,
  QUIZ_VOCAB_ROUTE,
  RECORDINGS_ROUTE,
  SIGNIN_ROUTE,
  SIMPLE_LOGIN_ROUTE,
} from '@common/constants/routes'
import { NavLink as NavLinkRouter, RouterLink, useLocation } from '@lib/router'
import { useToast } from '@features/toast/hooks'
import ProfileMenu from './profile-menu'
import unswLogo from '@assets/images/mentorai_white.svg'
import pengguinLogo from '@assets/svg/pengguin-logo.svg'
import ispekLogoMobile from '@assets/svg/pengguin-logo-mobile.svg'
import ispekLogoDarkMobile from '@assets/svg/ispeak-mobile-dark.svg'
import isepakLogoDark from '@assets/svg/logo-speakduo-dark.svg'
import { Notifications } from '@features/notifications/components'
import { usePaymentGetCreditsQuery } from '@app/services/auto-generated/payment'
import { useUser } from '@features/auth/hooks'
import { isUNSW } from '@utils'
import { useTheme } from '@features/theme/theme.hooks'
import { creditsSystem } from '@config/features-flags'
import { FlotingButton } from './header.styles'
import { ArrowOutwardIcon } from '@common/icons'
import { ThemeProvider } from '@common/utils/styles'
import darkTheme from '@styles/themes/dark-theme'
import {
  AttendanceLimitsResponse,
  useNeopaymentGetAttendanceLimitsQuery,
} from '@app/services/auto-generated/neopayment'
import { useAuthWhoamiQuery } from '@app/services/auto-generated/auth'
import { selectUser } from '@features/auth/auth-slice'

export const navlinks = [
  // { label: 'Businesses', to: 'https://www.ispeakto.com/corporate' },
  // { label: 'Institutions', to: 'https://ispeakto.com/institutions' },
]

const Header = () => {
  const matches = useMediaQuery(990)
  const { t, i18n } = useTranslation('homepage')
  const { pathname } = useLocation()
  const { data: limits } = useNeopaymentGetAttendanceLimitsQuery()
  const { data } = usePaymentGetCreditsQuery(undefined, { skip: !creditsSystem })
  const toast = useToast()
  const user = useAppSelector(selectUser)
  const { isStudent } = useUser()
  const handleClick = () => {
    toast('This feature is not available at the moment', 'warning')
  }
  const [alignment, setAlignment] = React.useState('en')

  const handleChange = (event: React.MouseEvent<HTMLElement>, newAlignment: string) => {
    setAlignment(newAlignment)
    i18n.changeLanguage(newAlignment)
  }

  return (
    <>
      <AppBar position='relative' color='transparent' elevation={0} sx={{ zIndex: 10 }}>
        {user?.plan === 'free' && user?.status === 'STUDENT' && <UpgradeBanner limits={limits} />}
        <Toolbar
          component={Container}
          maxWidth='xl'
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Stack direction='row' spacing={2} alignItems='center'>
            <Logo />
            {!matches && (
              <Stack direction='row' spacing={1}>
                {navlinks.map((navlink, k) => (
                  <NavLink
                    key={k}
                    color='neutral'
                    label={navlink.label}
                    to={navlink.to}
                    onClick={navlink.label === 'PengguinTV' ? handleClick : undefined}
                  />
                ))}
              </Stack>
            )}
          </Stack>
          <Stack direction='row' spacing={1} alignItems='center'>
            {!user ? (
              <>
                {pathname !== MAIN_ROUTE ? (
                  <ToggleButtonGroup
                    color='primary'
                    value={alignment}
                    exclusive
                    onChange={handleChange}
                    aria-label='Platform'
                  >
                    <ToggleButton value='en'>
                      <img
                        src={'https://flagicons.lipis.dev/flags/4x3/gb.svg'}
                        alt='flag'
                        style={{ width: '25px' }}
                      />
                    </ToggleButton>
                    <ToggleButton value='fr'>
                      <img
                        src={'https://flagicons.lipis.dev/flags/4x3/fr.svg'}
                        alt='flag'
                        style={{ width: '25px' }}
                      />
                    </ToggleButton>
                    <ToggleButton value='es'>
                      <img
                        src={'https://flagicons.lipis.dev/flags/4x3/es.svg'}
                        alt='flag'
                        style={{ width: '25px' }}
                      />
                    </ToggleButton>
                  </ToggleButtonGroup>
                ) : null}
                <FlotingButton
                  color='neutral'
                  variant='contained'
                  component={RouterLink}
                  to={isUNSW() ? LOGIN_ROUTE : SIMPLE_LOGIN_ROUTE}
                  sx={{
                    fontSize: '14px',
                    color: 'white',
                    width: '90px',
                    height: '40px',
                    bgcolor: '#528be7',
                  }}
                >
                  {t('header.signIn', 'Sign In')}
                </FlotingButton>
              </>
            ) : (
              <>
                {!matches && (
                  <>
                    {creditsSystem && (
                      <Button variant='outlined' component={RouterLink} to={PRICING_ROUTE}>
                        {data ? data.credits : '-'} {t('header.credits', 'Credits')}
                      </Button>
                    )}
                    {!pathname.startsWith(HOME_ROUTE) && (
                      <Stack
                        direction='row'
                        spacing={1}
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%,-50%)',
                        }}
                      >
                        <NavLinkButton
                          link={MY_CLASSROOMS_ROUTE}
                          isActive={pathname.startsWith(MY_CLASSROOMS_ROUTE)}
                          bgColor='#FED363d3'
                          icon={duoPersonIcon}
                        >
                          Live Workshop
                        </NavLinkButton>
                        <NavLinkButton
                          link={isStudent ? RECORDINGS_ROUTE : NOTES_ROUTE}
                          isActive={
                            pathname.startsWith('/dashboard') &&
                            !pathname.startsWith(FEEDBACK_ROUTE) &&
                            !pathname.startsWith(QUIZ_VOCAB_ROUTE) &&
                            !pathname.startsWith(COMMUNITY_ROUTE)
                          }
                          bgColor='#D1CAFF'
                          width={15}
                          icon={personIcon}
                        >
                          AI Interview
                        </NavLinkButton>
                        <NavLinkButton
                          link={COMMUNITY_ROUTE}
                          isActive={pathname.startsWith(COMMUNITY_ROUTE)}
                          bgColor='#A8CAFF'
                          width={17}
                          icon={homeIcon}
                        >
                          Community
                        </NavLinkButton>
                        <NavLinkButton
                          link={FEEDBACK_ROUTE}
                          isActive={pathname.startsWith(FEEDBACK_ROUTE)}
                          bgColor='#FFCCC9'
                          width={15}
                          icon={feedbackIcon}
                        >
                          AI Feedback
                        </NavLinkButton>
                        <NavLinkButton
                          link={QUIZ_VOCAB_ROUTE}
                          isActive={pathname.startsWith(QUIZ_VOCAB_ROUTE)}
                          bgColor='#A7E4B7'
                          width={15}
                          icon={rulerIcon}
                        >
                          Exercises
                        </NavLinkButton>
                      </Stack>
                    )}
                    {/* <ThemeProvider theme={darkTheme}>
                    <Button
                      floating={!pathname.startsWith('/dashboard')}
                      color={'neutral'}
                      variant='outlined'
                      component={RouterLink}
                      to={isStudent ? (isUNSW() ? DASHBOARD_ROUTE : QUIZ_VOCAB_ROUTE) : NOTES_ROUTE}
                      sx={{
                        fontWeight: 600,
                        backgroundColor: pathname.startsWith('/dashboard') ? '#3D474F' : '',
                        border: pathname.startsWith('/dashboard')
                          ? '1px solid rgba(80,70,70,0.5)'
                          : '',
                        ':hover': {
                          backgroundColor: pathname.startsWith('/dashboard') ? '#3D474F' : '',
                          border: pathname.startsWith('/dashboard')
                            ? '1px solid rgb(80,70,70)'
                            : '',
                        },
                      }}
                    >
                      {t('header.my_dashboard', 'My Dashboard')}
                    </Button>
                  </ThemeProvider>
                  <Button
                    disableRipple
                    color='secondary'
                    variant='outlined'
                    floating={!pathname.startsWith('/events')}
                    component={RouterLink}
                    to={MY_CLASSROOMS_ROUTE}
                    sx={{
                      fontWeight: 600,
                      color: 'black',
                      backgroundColor: pathname.startsWith('/events') ? '#FED363' : '',
                      border: pathname.startsWith('/events') ? '1px solid rgba(80,70,70,0.5)' : '',
                      ':hover': {
                        backgroundColor: pathname.startsWith('/events') ? '#FED363' : '',
                        color: 'black',
                        border: pathname.startsWith('/events') ? '1px solid rgb(80,70,70)' : '',
                      },
                    }}
                  >
                    {t('header.classrooms.explore_events', 'Explore Events')}
                    <ArrowOutwardIcon sx={{ fontSize: '14px' }} />
                  </Button> */}
                  </>
                )}
                <Notifications />
                <ProfileMenu />
              </>
            )}
          </Stack>
        </Toolbar>
      </AppBar>
    </>
  )
}

const UpgradeBanner = (props: { limits: AttendanceLimitsResponse | undefined }) => {
  const { limits } = props
  const isSmDown = useMediaQuery(600)
  if (!limits) return null
  // if (limits.current_count < 3) return null
  return (
    <Stack
      direction='row'
      justifyContent='center'
      alignItems='center'
      spacing={2}
      sx={{
        width: '100%',
        p: 1,
        backgroundColor: 'primary.main',
        color: 'white',
      }}
    >
      {isSmDown ? (
        <Typography variant='body2' sx={{ fontSize: { xs: '12px', md: '14px' } }}>
          <span style={{ fontWeight: 'bolder' }}>{limits.remaining_count}</span> events left.
          <br /> <span style={{ fontWeight: 'bolder' }}>Upgrade</span> for unlimited access!
        </Typography>
      ) : (
        <Typography variant='body2'>
          You have <span style={{ fontWeight: 'bolder' }}>{limits.remaining_count}</span>{' '}
          {limits.remaining_count === 1 ? ' event ' : ' events '} left this month. Upgrade to{' '}
          <span style={{ fontWeight: 'bolder' }}>SpeakDuo Premium</span> for unlimited live events{' '}
        </Typography>
      )}
      <Button
        component={RouterLink}
        to={PRICING_ROUTE}
        variant='outlined'
        color='light'
        size='small'
        sx={{ color: 'white' }}
      >
        Upgrade
      </Button>
    </Stack>
  )
}

export const Logo = (props: { style?: React.CSSProperties }) => {
  const { isDark } = useTheme()
  const { user } = useUser()
  const { pathname } = useLocation()
  const isMatch = useMediaQuery(1250)
  const isSMDown = useMediaQuery(600)
  const getLogo = () => {
    if (isUNSW()) return unswLogo
    const isClassroomPath = pathname.startsWith('/classroom/')
    const isHomeOrEvents = pathname === '/' || pathname.startsWith('/events')
    if (!isHomeOrEvents && isDark) {
      return isClassroomPath && isSMDown ? ispekLogoDarkMobile : isepakLogoDark
    }
    return isClassroomPath && isSMDown ? ispekLogoMobile : pengguinLogo
  }

  const LogoImage = () => (
    <img
      src={getLogo()}
      alt={'ispeak'}
      style={{
        height: isMatch ? '22px' : '34px',
        transform: 'translateY(2px)',
        ...props.style,
      }}
    />
  )

  const isEventsPage = pathname.startsWith('/events')

  return (
    <Stack direction='row' spacing={2} alignItems='center'>
      <RouterLink to={user ? HOME_ROUTE : LANDING_ROUTE}>
        <LogoImage />
      </RouterLink>
      {!pathname.startsWith(PRICING_ROUTE) && !pathname.startsWith(EVENT_ROUTE) && (
        <Link
          component={RouterLink}
          to={PRICING_ROUTE}
          sx={{
            ml: 3,
            color: (theme) => {
              if (isEventsPage || pathname === MAIN_ROUTE) return 'black'
              return isDark ? theme.palette.text.primary : 'text.secondary'
            },
            textDecoration: 'none',
            fontSize: { xs: '13px', md: '14px' },
            fontWeight: 500,
            '&:hover': {
              color: (theme) => {
                if (isEventsPage) return theme.palette.primary.main
                return isDark ? theme.palette.primary.main : theme.palette.primary.main
              },
            },
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          Pricing
        </Link>
      )}
    </Stack>
  )
}

export const NavLink = <RouterLink extends React.ElementType>(
  props: ButtonProps<RouterLink, { component?: RouterLink }> & { label: string },
) => {
  const { label } = props
  return (
    <Button
      color='neutral'
      component={NavLinkRouter}
      variant='text'
      sx={{
        px: 3,
        py: 0.5,
        fontSize: '16px',
        borderRadius: '10px',
        color: 'rgba(0, 0, 0, 0.87)',
      }}
      style={({ isActive }) => (isActive ? { backgroundColor: 'rgba(0, 0, 0, 0.08)' } : undefined)}
      {...props}
    >
      {label}
    </Button>
  )
}

export const NavLinkButton: React.FC<
  {
    isActive?: boolean
    icon: string
    width?: string | number
    bgColor: string
    link: string
  } & Omit<ButtonProps, 'isActive'>
> = (props) => {
  const { icon, isActive, link, bgColor, width, children, ...buttonProps } = props
  const { isDark } = useTheme()
  const { pathname } = useLocation()
  const inEvents = pathname.startsWith(MY_CLASSROOMS_ROUTE)
  const isMatch = useMediaQuery(1250)
  return (
    <Button
      size={isMatch ? 'small' : 'medium'}
      component={RouterLink}
      to={link}
      color='neutral'
      variant='outlined'
      {...buttonProps}
      sx={{
        bgcolor: inEvents ? (theme) => theme.palette.background.paper : 'initial',
        ':hover': {
          bgcolor: isActive
            ? bgColor
            : inEvents
            ? (theme) => theme.palette.background.paper
            : 'initial',
        },
        backdropFilter: 'blur(10px)',
        ...(isActive && {
          color: isDark ? 'black' : 'initial',
          bgcolor: bgColor,
        }),
        ...buttonProps.sx,
      }}
    >
      <Box
        component='img'
        src={icon}
        alt='nav-button'
        sx={{
          width: width ? width : 25,
          mr: 0.5,
          filter: isDark ? 'brightness(0)  invert(1)' : ' brightness(0.27) saturate(0%) invert(0%)',
          ...(isActive && {
            filter: 'none',
          }),
        }}
      />
      <Typography noWrap sx={{ fontSize: { md: '14px', lg: '16px' } }}>
        {children}
      </Typography>
    </Button>
  )
}

export default Header
